import Route from '@ember/routing/route';
import { hash } from 'rsvp';
import { get } from '@ember/object';
import { service } from '@ember/service';
import { routeEvent } from 'garaje/utils/decorators/route';
import throwUnlessTaskDidCancel from 'garaje/utils/throw-unless-task-did-cancel';

class EmployeesSyncSettingsRoute extends Route {
  @service abilities;
  @service authz;
  @service currentLocation;
  @service integrationsManager;
  @service router;
  @service state;
  @service store;
  @service locations;
  @service scimPrimaryLocation;

  titleToken = 'Sync settings';

  beforeModel() {
    if (this.abilities.cannot('visit sync-settings')) {
      this.router.transitionTo('unauthorized');
    }
  }

  async model() {
    if (!this.authz.roles || this.authz.roles.length < 1) {
      await this.authz.fetchRoles();
    }

    const { currentLocation } = this.state;
    this.locations.loadAllTask.perform().catch(throwUnlessTaskDidCancel);
    await this.currentLocation.loadIntegrations.perform();

    let employeeGroupRoles;
    try {
      employeeGroupRoles = await this.store.findAll('employee-group-role', {
        include: 'locations,employee-group',
      });
    } catch (error) {
      // will return an adapter error wrapping a 403 result if lacking permission (e.g. location admin)
      const is403Error = error.isAdapterError && error.message?.includes('returned a 403');
      if (!is403Error) {
        throw error;
      }
      employeeGroupRoles = [];
    }

    let scimPrimaryLocationConfig = undefined;
    let scimPrimaryLocationMappings = [];
    const scimIntegration = await get(this.currentLocation, 'company.scimIntegration');
    if (scimIntegration) {
      scimPrimaryLocationConfig = await scimIntegration.scimPrimaryLocationConfiguration;
    }

    if (scimPrimaryLocationConfig?.id) {
      await this.scimPrimaryLocation.fetchDistinctMappingValues(scimPrimaryLocationConfig.id);
      scimPrimaryLocationMappings = await this.store.findAll('scim-primary-location-mapping', {
        reload: true,
      });

      this.createPrimaryLocationMappings(scimPrimaryLocationMappings, scimPrimaryLocationConfig);
    }

    return hash({
      plugins: this.store.query('plugin', {
        filter: { location: currentLocation.id },
      }),
      pluginInstalls: this.store.query('plugin-install', {
        filter: { location: currentLocation.id },
        page: { limit: 100 },
      }),
      employeeGroupRoles,
      scimPrimaryLocationConfig,
      scimPrimaryLocationMappings,
    });
  }

  afterModel(model) {
    const syncPlugins = model.plugins.filterBy('category', 'sync');
    const syncPluginInstalls = model.pluginInstalls.filter((install) => {
      return syncPlugins.findBy('id', get(install, 'plugin.id')) !== undefined;
    });
    const hasPlatform =
      syncPluginInstalls.filter(function (install) {
        return get(install, 'status') !== 'uninstalled';
      }).length > 0;

    if (!this.integrations.length && !hasPlatform) {
      this.router.transitionTo('employees.integrations');
    }
  }

  deactivate() {
    super.deactivate(...arguments);
    this.controller.integration = '';
  }

  confirmAction() {
    return window.confirm('You will lose any unsaved changes. Are you sure you want to continue?');
  }

  get integrations() {
    return this.integrationsManager.getIntegrations(this.currentLocation);
  }

  createPrimaryLocationMappings(scimPrimaryLocationMappings, scimPrimaryLocationConfig) {
    const unmappedValues = this.scimPrimaryLocation.unmappedDistinctValues(scimPrimaryLocationMappings);
    unmappedValues.forEach((value) => {
      if (scimPrimaryLocationMappings.some((mapping) => mapping.isNew && mapping.scimAttributeValue === value)) return;

      const primaryLocationMapping = this.store.createRecord('scim-primary-location-mapping', {
        scimAttributeValue: value,
      });
      primaryLocationMapping.scimPrimaryLocationConfiguration = scimPrimaryLocationConfig;
    });
  }

  @routeEvent
  routeWillChange(transition) {
    const currentLocation = this.currentLocation;
    const hasDirtyIntegrations = this.integrationsManager.hasDirtyIntegrations(currentLocation);

    if (hasDirtyIntegrations) {
      if (this.confirmAction()) {
        this.integrationsManager.cleanIntegrations(currentLocation);

        return true;
      } else {
        transition.abort();

        return false;
      }
    } else {
      return true;
    }
  }
}

export default EmployeesSyncSettingsRoute;
